/**
 * Paddle.js, loaded once and only when a checkout is about to open.
 *
 * The script comes from Paddle's CDN (that is how Paddle.js is distributed; the npm package is a
 * typed loader for it), so loading it lazily keeps a third-party script off every other page of
 * the dashboard.
 *
 * Only the public client token and environment are used here. Every checkout is opened for a
 * transaction Paddle or the Platform API created, so the price, the workspace and the customer are
 * fixed before this code runs -- nothing the browser does here can change what is bought or for
 * whom.
 */
import { initializePaddle, type Paddle } from '@paddle/paddle-js';
import { env } from '../env';

let loading: Promise<Paddle> | null = null;

/**
 * The page currently interested in checkout events.
 *
 * Paddle.js takes one event callback at initialisation and keeps it for the life of the page, so
 * events are routed through this rather than captured by whichever page loaded the script first.
 */
let listener: ((eventName: string) => void) | null = null;

/** Whether a checkout overlay is showing, as Paddle.js last reported it. */
let checkoutVisible = false;

function load(): Promise<Paddle> {
  const token = env.PUBLIC_PADDLE_CLIENT_TOKEN;
  const environment = env.PUBLIC_PADDLE_ENVIRONMENT;
  if (token === undefined || environment === undefined) {
    return Promise.reject(new Error('Billing is not configured in this environment.'));
  }

  loading ??= initializePaddle({
    token,
    environment,
    eventCallback: (event) => {
      if (event.name === undefined) return;
      const name = String(event.name);
      if (name === 'checkout.loaded') checkoutVisible = true;
      if (name === 'checkout.closed') checkoutVisible = false;
      listener?.(name);
    },
  }).then((paddle) => {
    if (paddle === undefined) throw new Error('Paddle checkout could not be loaded.');
    return paddle;
  });

  // A failed load (blocked by an extension, offline) must not be cached for the rest of the visit.
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/**
 * Open Paddle's overlay checkout for a transaction the Platform API created.
 *
 * `completed` fires when Paddle reports the payment done. That is a hint to start watching for
 * the plan to change -- not the change itself, which only a verified webhook makes.
 */
export async function openCheckout(input: {
  transactionId: string;
  email: string | undefined;
  completed: () => void;
  closed?: () => void;
}): Promise<void> {
  listener = (eventName) => {
    if (eventName === 'checkout.completed') input.completed();
    if (eventName === 'checkout.closed') input.closed?.();
  };
  const paddle = await load();
  paddle.Checkout.open({
    transactionId: input.transactionId,
    ...(input.email === undefined ? {} : { customer: { email: input.email } }),
    settings: { displayMode: 'overlay', theme: 'light' },
  });
}

/**
 * How long to wait for Paddle.js to open a payment link's checkout by itself.
 *
 * Paddle.js opens the checkout named by `?_ptxn=` when it initialises on a payment-link page. This
 * page cannot tell in advance whether that will happen, and opening it a second time would stack
 * two overlays, so it waits for the checkout to announce itself and opens it only if it does not.
 */
const AUTO_OPEN_GRACE_MS = 2_000;

/** One attempt per payment link, however many times the page mounts. */
const paymentLinks = new Map<string, Promise<void>>();

/**
 * Open the checkout a Paddle payment link points at.
 *
 * Paddle sends customers to the default payment link with the transaction in `?_ptxn=` -- from its
 * emails, including the one asking a customer whose renewal failed to pay again. The transaction
 * already names the customer and the price; this only puts the checkout in front of them.
 *
 * Shared per transaction because React mounts effects twice in development: two independent
 * attempts would each wait out the grace period and each open an overlay.
 */
export function openPaymentLink(input: {
  transactionId: string;
  completed: () => void;
  closed: () => void;
}): Promise<void> {
  // The most recent mount receives the events; an unmounted one has nothing left to update.
  listener = (eventName) => {
    if (eventName === 'checkout.completed') input.completed();
    if (eventName === 'checkout.closed') input.closed();
  };

  const existing = paymentLinks.get(input.transactionId);
  if (existing !== undefined) return existing;

  const attempt = (async () => {
    const paddle = await load();
    await new Promise((resolve) => setTimeout(resolve, AUTO_OPEN_GRACE_MS));
    if (!checkoutVisible) {
      paddle.Checkout.open({
        transactionId: input.transactionId,
        settings: { displayMode: 'overlay', theme: 'light' },
      });
    }
  })();

  paymentLinks.set(input.transactionId, attempt);
  // A failed attempt may be retried; a successful one never needs to be.
  attempt.catch(() => paymentLinks.delete(input.transactionId));
  return attempt;
}
